<template>
  <!-- HEADER: solo posiciona -->
  <header class="fixed top-4 left-0 right-0 z-50 flex justify-center px-4 md:px-10">
    <!-- NAV: contenedor visual flotante -->
    <nav
      class="relative flex items-center w-full max-w-7xl h-14 md:h-16 bg-white/95 backdrop-blur-md rounded-lg shadow-lg px-4 md:px-8 transition-all duration-300"
    >
      <!-- Mobile Navbar-->
      <div class="flex items-center justify-between w-full md:hidden">
        <!-- Izquierda: hamburguesa -->
        <button
          @click="toggleMobileMenu"
          ref="hamburgerButton"
          aria-label="Menú principal"
          class="p-2 rounded-lg hover:bg-gray-100 transition text-gray-700"
        >
          <!-- Ícono animado: 3 barras → X -->
          <span class="flex flex-col gap-[5px] w-5">
            <span
              class="block h-[2px] bg-gray-800 rounded transition-all duration-300 origin-center"
              :class="isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''"
            ></span>
            <span
              class="block h-[2px] bg-gray-800 rounded transition-all duration-300"
              :class="isMobileMenuOpen ? 'opacity-0 scale-x-0' : ''"
            ></span>
            <span
              class="block h-[2px] bg-gray-800 rounded transition-all duration-300 origin-center"
              :class="isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''"
            ></span>
          </span>
        </button>

        <!-- Centro: logo -->
        <RouterLink to="/" @click="closeAll" class="absolute left-1/2 -translate-x-1/2">
          <img src="@/assets/inbros-logotipo3.png" alt="INBROS" class="h-7" />
        </RouterLink>

        <!-- Derecha: ícono login -->
        <RouterLink
          to="/login"
          @click="closeAll"
          aria-label="Iniciar sesión"
          class="p-2 rounded-lg hover:bg-gray-100 transition text-gray-700"
        >
          <i class="ri-user-line text-xl"></i>
        </RouterLink>
        <!-- Derecha: ícono lupa -->
        <!-- <button
          @click="toggleSearch"
          aria-label="Abrir buscador"
          class="p-2 rounded-lg hover:bg-gray-100 transition text-gray-700"
        >
          <i class="ri-search-line text-xl"></i>
        </button> -->
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 -translate-y-3"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-3"
        >
          <div
            v-if="isMobileMenuOpen"
            id="mobile-menu-container"
            class="md:hidden absolute top-[4.5rem] left-4 right-4 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden"
          >
            <!-- Fila: idioma + separador -->
            <div class="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span class="text-xs text-gray-400 uppercase tracking-widest">{{ $t('navbar.lang') }}</span>
              <button
                @click="toggleLanguage"
                class="text-xs font-semibold text-gray-600 hover:text-[#F37021] transition bg-gray-100 hover:bg-orange-50 rounded-md px-2.5 py-1"
              >
                {{ languageLabel }}
              </button>
            </div>

            <!-- Item: Servicios con acordeón -->
            <div>
              <button
                @click="toggleServicesAccordion"
                class="flex items-center justify-between w-full px-5 py-4 text-sm font-semibold text-gray-800 hover:text-[#F37021] transition hover:bg-gray-50"
              >
                <span>{{ $t('navbar.services') }}</span>
                <i
                  class="ri-arrow-down-s-line text-lg transition-transform duration-200"
                  :class="isServicesOpen ? 'rotate-180 text-[#F37021]' : 'text-gray-400'"
                ></i>
              </button>

              <!-- Sub-items con altura animada -->
              <Transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 max-h-0"
                enter-to-class="opacity-100 max-h-32"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 max-h-32"
                leave-to-class="opacity-0 max-h-0"
              >
                <div v-if="isServicesOpen" class="overflow-hidden bg-gray-50">
                  <RouterLink
                    to="/personal"
                    @click="closeAll"
                    class="flex items-center gap-3 px-8 py-3 text-sm text-gray-600 hover:text-[#F37021] hover:bg-orange-50 transition border-b border-gray-100"
                  >
                    <i class="ri-user-line text-base text-gray-400"></i>
                    {{ $t('navbar.personal') }}
                  </RouterLink>
                  <RouterLink
                    to="/empresas"
                    @click="closeAll"
                    class="flex items-center gap-3 px-8 py-3 text-sm text-gray-600 hover:text-[#F37021] hover:bg-orange-50 transition"
                  >
                    <i class="ri-building-2-line text-base text-gray-400"></i>
                    {{ $t('navbar.empresas') }}
                  </RouterLink>
                </div>
              </Transition>
            </div>

            <!-- Item: Acerca de -->
            <RouterLink
              to="/about"
              @click="closeAll"
              class="flex items-center gap-3 px-5 py-4 text-sm font-semibold text-gray-800 hover:text-[#F37021] hover:bg-gray-50 transition border-t border-gray-100"
            >
              <i class="ri-information-line text-base text-gray-400"></i>
              {{ $t('navbar.about') }}
            </RouterLink>
          </div>
        </Transition>
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
        >
          <ul
            v-if="searchQuery && filteredRoutes.length"
            class="absolute top-[4.5rem] right-4 md:right-10 w-64 bg-white shadow-lg rounded-xl z-50 max-h-60 overflow-auto border border-gray-100"
          >
            <li
              v-for="option in filteredRoutes"
              :key="option.label"
              @click="navigateTo(option.path)"
              class="px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-[#F37021] cursor-pointer transition border-b border-gray-50 last:border-0"
            >
              {{ option.label }}
            </li>
          </ul>
        </Transition>
      </div>
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-y-100"
        leave-to-class="opacity-0 scale-y-95"
      >
        <div
          v-if="isSearchOpen"
          class="md:hidden absolute inset-0 bg-white rounded-lg flex items-center px-4 gap-3 z-10"
        >
          <button @click="toggleSearch" class="text-gray-400 hover:text-gray-700 transition shrink-0">
            <i class="ri-arrow-left-line text-xl"></i>
          </button>
          <input
            ref="searchInputMobile"
            type="text"
            v-model="searchQuery"
            @keydown.enter="onKeyEnter"
            :placeholder="$t('navbar.search')"
            class="flex-1 bg-transparent text-gray-800 outline-none text-sm placeholder:text-gray-500"
          />
          <RouterLink
            to="/login"
            @click="closeAll"
            class="shrink-0 text-sm font-semibold text-gray-800 hover:text-[#F37021] transition whitespace-nowrap border-l border-gray-200 pl-3"
          >
            {{ $t('navbar.login') }}
          </RouterLink>
        </div>
      </Transition>

      <!-- Desktop Navbar-->
      <div class="hidden md:flex items-center flex w-full">
        <!-- IZQUIERDA: idioma + servicios -->
        <div class="flex items-center gap-6 shrink-0">
          <span @click="toggleLanguage" class="cursor-pointer hover:text-[#F37021] transition">
            {{ languageLabel }}
          </span>
        </div>
        <!-- Espaciador -->
        <div class="flex-1 flex justify-center">
          <button @click="toggleMenu" ref="menuButton" class="text-lg font-medium hover:text-[#F37021] transition">
            {{ $t('navbar.services') }}
          </button>
        </div>

        <!-- CENTRO: logo -->
        <div class="flex items-center justify-center shrink-0">
          <RouterLink to="/" @click="handleLogoClick">
            <img src="@/assets/inbros-logotipo3.png" alt="Logo INBROS" class="h-7" />
          </RouterLink>
        </div>

        <!-- Espaciador: acerca -->
        <div class="flex-1 flex justify-center">
          <RouterLink
            to="/about"
            class="hidden md:block text-lg font-medium hover:text-[#F37021] transition whitespace-nowrap"
          >
            {{ $t('navbar.about') }}
          </RouterLink>
        </div>
        <!-- Derecha: buscador + login -->
        <div class="flex items-center shrink-0">
          <div
            class="group flex items-center gap-1 bg-white/90 px-4 py-2 rounded-lg shadow-[0_10px_20px_rgba(0,0,0,0.2),_0_6px_6px_rgba(0,0,0,0.1)] hover:shadow-lg hover:bg-white transition-all backdrop-blur-sm"
          >
            <i class="ri-search-line text-xl text-black group-hover:text-[#F37021]"></i>

            <input
              name="buscador"
              type="text"
              v-model="searchQuery"
              @keydown.enter="onKeyEnter"
              :placeholder="$t('navbar.search')"
              class="bg-transparent text-black outline-none w-0 group-hover:w-40 group-hover:ml-1 transition-all duration-300 ease-out placeholder:text-gray-500"
            />
            <div class="w-[1px] h-4 bg-black/10 mx-2 group-hover:block hidden"></div>
            <!-- BOTÓN LOGIN (NO SE TOCA visualmente) -->
            <RouterLink
              to="/login"
              class="text-black font-semibold hover:text-orange-600 transition-colors whitespace-nowrap ml-1"
            >
              {{ $t('navbar.login') }}
            </RouterLink>
          </div>
        </div>

        <!-- LOGIN MOBILE -->
        <RouterLink
          to="/login"
          class="sm:hidden bg-white/90 text-black text-xs font-medium rounded-lg px-3 py-1.5 shadow-md hover:bg-white transition backdrop-blur-md"
        >
          {{ $t('navbar.login') }}
        </RouterLink>

        <!-- Menú principal -->
        <div
          v-if="isMenuOpen"
          id="menu-container"
          class="fixed top-[4.5rem] left-4 md:left-[9.5rem] bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.15)] px-4 py-3 transition-all duration-300 opacity-100 translate-y-0"
        >
          <div class="flex gap-3">
            <template v-for="item in menuItems" :key="item.key">
              <RouterLink
                v-if="item.key === 'empresas'"
                to="/empresas"
                class="text-sm font-medium text-gray-600 hover:text-[#F37021] transition px-2 py-1 rounded"
              >
                {{ item.label }}
              </RouterLink>
              <RouterLink
                v-if="item.key === 'personal'"
                to="/personal"
                class="text-sm font-medium text-gray-600 hover:text-[#F37021] transition px-2 py-1 rounded"
              >
                {{ item.label }}
              </RouterLink>
            </template>
          </div>
        </div>
        <!-- Resultados de búsqueda -->
        <ul
          v-if="searchQuery && filteredRoutes.length"
          class="fixed top-[5rem] right-6 w-64 bg-white shadow-md rounded-md z-50 max-h-64 overflow-auto"
        >
          <li
            v-for="option in filteredRoutes"
            :key="option.label"
            @click="navigateTo(option.path)"
            class="px-4 py-2 text-sm text-[#101541] hover:bg-gray-100 cursor-pointer"
          >
            {{ option.label }}
          </li>
        </ul>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { loadLocale } from '@/i18n/i18n'
import { siteContent, translationMap } from '@/data/searchData'

const router = useRouter()
const searchQuery = ref('')
const isMenuOpen = ref(false)
const isMobileMenuOpen = ref(false)
const isServicesOpen = ref(false)
const isSearchOpen = ref(false)
const submenu = ref(null)
const { locale, t, tm } = useI18n()

// Computed label for the toggle element (ES or EN)
const languageLabel = computed(() => (locale.value === 'es' ? 'EN' : 'ES'))

// Click handler to flip the locale without showing a dropdown
async function toggleLanguage() {
  const newLocale = locale.value === 'es' ? 'en' : 'es'
  await loadLocale(newLocale)
}

const menuItems = computed(() => [
  { key: 'personal', label: t('navbar.personal') },
  { key: 'empresas', label: t('navbar.empresas') }
])

const filteredRoutes = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return []

  return siteContent
    .map((item) => {
      // 1. Obtenemos el Label traducido usando tu translationMap
      const translationKey = translationMap[item.path]
      const translatedLabel = translationKey ? t(translationKey) : item.label

      // 2. Obtenemos el Array de keywords desde el JSON
      // Usamos tm (translation map) porque es un array de strings
      const translatedKeywords = tm(item.keywords) || []

      return {
        ...item,
        label: translatedLabel,
        // Guardamos las keywords traducidas para el filtro
        activeKeywords: translatedKeywords
      }
    })
    .filter((page) => {
      // 3. Buscamos coincidencia
      const matchLabel = page.label.toLowerCase().includes(query)

      // Verificamos que activeKeywords sea un array antes de hacer .some
      const matchKeywords = Array.isArray(page.activeKeywords)
        ? page.activeKeywords.some((kw) => kw.toLowerCase().includes(query))
        : false

      return matchLabel || matchKeywords
    })
})

const navigateTo = (path) => {
  searchQuery.value = ''
  isSearchOpen.value = false
  router.push(path)
}

const onKeyEnter = () => {
  if (searchQuery.value.trim()) {
    router.push({ path: '/busqueda', query: { query: searchQuery.value } })
    searchQuery.value = ''
  }
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  submenu.value = null
}

// const toggleSubmenu = (key) => {
//   submenu.value = submenu.value === key ? null : key;
// };

// const handleLogoClick = () => {
//   isMenuOpen.value = false;
//   submenu.value = null;
// };

// function closeMenu() {
//   isMenuOpen.value = false;
//   submenu.value = null;
// }

const menuButton = ref(null)
const hamburgerButton = ref(null)
const searchInputMobile = ref(null)

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  // Cerrar acordeón al cerrar el panel
  if (!isMobileMenuOpen.value) isServicesOpen.value = false
}

function toggleServicesAccordion() {
  isServicesOpen.value = !isServicesOpen.value
}

async function toggleSearch() {
  isSearchOpen.value = !isSearchOpen.value
  if (isSearchOpen.value) {
    // Foco automático al abrir
    await nextTick()
    searchInputMobile.value?.focus()
  } else {
    searchQuery.value = ''
  }
}

function handleLogoClick() {
  closeAll()
}

function closeAll() {
  isMenuOpen.value = false
  isMobileMenuOpen.value = false
  isServicesOpen.value = false
  isSearchOpen.value = false
  searchQuery.value = ''
}

function handleGlobalClick(e) {
  setTimeout(() => {
    const menuEl = document.getElementById('menu-container')
    const mobileMenu = document.getElementById('mobile-menu-container')

    const clickedMenu = menuEl?.contains(e.target)
    const clickedHamburger = hamburgerButton.value?.contains(e.target)
    const clickedButton = menuButton.value?.contains(e.target)
    const clickedMobile = mobileMenu?.contains(e.target)
    const clickedBtnM = menuButton.value?.contains(e.target)

    if (!clickedMenu && !clickedHamburger && !clickedButton && !clickedMobile && !clickedBtnM) {
      isMenuOpen.value = false
      isMobileMenuOpen.value = false
      isServicesOpen.value = false
    }
  }, 0)
}

function handleScroll() {
  isMenuOpen.value = false
  isMobileMenuOpen.value = false
  isServicesOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleGlobalClick)
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleGlobalClick)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
@import 'https://cdn.jsdelivr.net/npm/remixicon@3.4.0/fonts/remixicon.css';
</style>
